/**
 * reviews-form.js — Sprint 2
 * <review-form> web component: 3-step form (rating+title / body+photos+name / submit).
 * Supports guests AND logged-in customers. Photo upload with client-side resize
 * (≤1MB JPEG, max 5 photos). HMAC + nonce from window.reviewConfig.
 */

const cfg = () => window.reviewConfig || {};
const t   = key => window.reviewTranslations?.[key] ?? key;

const MAX_PHOTOS_DEFAULT = 5;
const MAX_PHOTO_BYTES_DEFAULT = 1024 * 1024;
const MAX_PHOTO_DIMENSION = 1600;

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ── Draft persistence ────────────────────────────────────────

class ReviewDraft {
  static #key(productId) { return `padelneeds_review_draft_${productId}`; }
  static get(productId)  { try { return JSON.parse(localStorage.getItem(this.#key(productId)) || 'null'); } catch { return null; } }
  static save(productId, data) { try { localStorage.setItem(this.#key(productId), JSON.stringify(data)); } catch {} }
  static clear(productId) { try { localStorage.removeItem(this.#key(productId)); } catch {} }
}

// ── Minimal focus trap ───────────────────────────────────────

class FocusTrap {
  #el; #first; #last; #bound;
  constructor(el) { this.#el = el; this.#bound = e => this.#trap(e); }
  activate() {
    const focusable = this.#el.querySelectorAll(
      'button:not([disabled]),input:not([disabled]),textarea:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'
    );
    this.#first = focusable[0];
    this.#last  = focusable[focusable.length - 1];
    document.addEventListener('keydown', this.#bound);
    this.#first?.focus();
  }
  deactivate() { document.removeEventListener('keydown', this.#bound); }
  #trap(e) {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === this.#first) { e.preventDefault(); this.#last?.focus(); }
    } else {
      if (document.activeElement === this.#last)  { e.preventDefault(); this.#first?.focus(); }
    }
  }
}

// ── Toast ────────────────────────────────────────────────────

function showToast(message, type = '') {
  let container = document.querySelector('.review-toast-container');
  if (!container) {
    container = document.createElement('div');
    container.className = 'review-toast-container';
    document.body.appendChild(container);
  }
  const toast = document.createElement('div');
  toast.className = `review-toast${type ? ` review-toast--${type}` : ''}`;
  toast.textContent = message;
  container.appendChild(toast);
  setTimeout(() => toast.remove(), 3000);
}

// ── Photo helpers ────────────────────────────────────────────

const ACCEPTED_PHOTO_MIME = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Resize an image File to ≤MAX_PHOTO_DIMENSION longest side and re-encode to JPEG ≤maxBytes.
 * Returns a new File. Uses canvas (no EXIF passthrough = strips metadata).
 */
async function resizePhoto(file, maxBytes) {
  if (!ACCEPTED_PHOTO_MIME.includes(file.type)) {
    throw new Error('photo_type_invalid');
  }
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) throw new Error('photo_decode_failed');

  const ratio = Math.min(1, MAX_PHOTO_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * ratio);
  const h = Math.round(bitmap.height * ratio);
  const canvas = (typeof OffscreenCanvas !== 'undefined')
    ? new OffscreenCanvas(w, h)
    : Object.assign(document.createElement('canvas'), { width: w, height: h });
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, w, h);

  // Encode at decreasing quality until under maxBytes (or quality floor)
  for (const q of [0.85, 0.75, 0.65, 0.55, 0.45]) {
    const blob = canvas.convertToBlob
      ? await canvas.convertToBlob({ type: 'image/jpeg', quality: q })
      : await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', q));
    if (blob && blob.size <= maxBytes) {
      return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
    }
  }
  throw new Error('photo_too_large');
}

// ── ReviewForm web component ─────────────────────────────────

class ReviewForm extends HTMLElement {
  #step    = 1;
  #rating  = 0;
  #photos  = []; // { file, previewUrl }
  #trap    = null;
  #overlay = null;
  #submitting = false;
  #successTimer = null;

  connectedCallback() {}

  open() {
    if (this.#overlay) return;
    this.#step    = 1;
    this.#rating  = 0;
    this.#photos  = [];
    this.#submitting = false;
    this.#render();
    const draft = ReviewDraft.get(cfg().productId);
    if (draft) { this.#fillDraft(draft); showToast(t('form_draft_restored')); }
    document.body.style.overflow = 'hidden';
    this.#trap = new FocusTrap(this.#overlay.querySelector('.review-form__drawer'));
    this.#trap.activate();
    document.addEventListener('keydown', this.#onEsc);
  }

  #onEsc = e => { if (e.key === 'Escape') this.close(); };

  close() {
    if (this.#submitting) return;
    if (this.#successTimer) {
      clearTimeout(this.#successTimer);
      this.#successTimer = null;
    }
    this.#saveDraft();
    this.#revokePhotoUrls();
    this.#overlay?.remove();
    this.#overlay = null;
    document.body.style.overflow = '';
    this.#trap?.deactivate();
    document.removeEventListener('keydown', this.#onEsc);
  }

  #revokePhotoUrls() {
    this.#photos.forEach(p => p.previewUrl && URL.revokeObjectURL(p.previewUrl));
  }

  #render() {
    this.#overlay = document.createElement('div');
    this.#overlay.className = 'review-form__overlay';
    this.#overlay.setAttribute('role', 'dialog');
    this.#overlay.setAttribute('aria-modal', 'true');
    this.#overlay.setAttribute('aria-label', t('form_title'));

    this.#overlay.innerHTML = `
      <div class="review-form__drawer">
        <div class="review-form__header">
          <h2 class="review-form__title">${escapeHtml(t('form_title'))}</h2>
          <button class="review-form__close" data-form-close aria-label="${escapeHtml(t('form_back'))}" type="button">✕</button>
        </div>
        <div class="review-form__progress" aria-hidden="true">
          <span class="review-form__progress-step is-active" data-progress-step="1"></span>
          <span class="review-form__progress-step" data-progress-step="2"></span>
        </div>
        ${this.#stepHTML(1)}
        ${this.#stepHTML(2)}
        <div class="review-form__success" hidden>
          <svg class="review-form__success-icon" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="11" fill="none" stroke="currentColor" stroke-width="2"/>
            <path d="M7 12l3 3 7-7" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <p class="review-form__success-title">${escapeHtml(t('form_success_title'))}</p>
          <p>${escapeHtml(t('form_success_message'))}</p>
        </div>
      </div>`;

    document.body.appendChild(this.#overlay);
    this.#activateStep(1);
    this.#bindEvents();
  }

  #bindEvents() {
    this.#overlay.addEventListener('click', e => {
      if (e.target === this.#overlay) this.close();
      if (e.target.closest('[data-form-close]')) this.close();
      if (e.target.closest('[data-form-next]'))   this.#nextStep();
      if (e.target.closest('[data-form-back]'))   this.#prevStep();
      if (e.target.closest('[data-form-submit]')) this.#submit(e);
      if (e.target.closest('[data-photo-remove]')) this.#removePhoto(e);

      const star = e.target.closest('[data-star]');
      if (star) {
        this.#rating = parseInt(star.dataset.star, 10);
        this.#updateStars();
      }
    });

    // Photo input
    const fileInput = this.#overlay.querySelector('#review-photos');
    if (fileInput) {
      fileInput.addEventListener('change', e => this.#handlePhotos(e.target.files));
    }

    // Live validation feedback
    const titleEl = this.#overlay.querySelector('#review-title');
    const bodyEl  = this.#overlay.querySelector('#review-body');
    const nameEl  = this.#overlay.querySelector('#review-name');
    titleEl?.addEventListener('input', () => this.#hideError('title-error'));
    bodyEl?.addEventListener('input',  () => this.#hideError('body-error'));
    nameEl?.addEventListener('input',  () => this.#hideError('name-error'));
  }

  #stepHTML(step) {
    if (step === 1) {
      const stars = Array.from({ length: 5 }, (_, i) =>
        `<button class="review-form__star" data-star="${i + 1}" type="button"
          aria-label="${i + 1} ${escapeHtml(t('stars') || 'stars')}" aria-pressed="false">★</button>`
      ).join('');

      return `
        <div class="review-form__step" data-step="1">
          <div class="review-form__field">
            <label class="review-form__label">${escapeHtml(t('form_rating_label'))} <span class="review-form__required">*</span></label>
            <div class="review-form__star-selector" role="group"
                 aria-label="${escapeHtml(t('form_rating_label'))}">${stars}</div>
            <p class="review-form__error" id="rating-error" hidden>${escapeHtml(t('form_rating_required'))}</p>
          </div>
          <div class="review-form__field">
            <label class="review-form__label" for="review-title">${escapeHtml(t('form_title_label'))} <span class="review-form__required">*</span></label>
            <input class="review-form__input" id="review-title" type="text" maxlength="120"
                   placeholder="${escapeHtml(t('form_title_placeholder') || '')}">
            <p class="review-form__error" id="title-error" hidden>${escapeHtml(t('form_title_required') || 'Please add a short title')}</p>
          </div>
          <div class="review-form__actions">
            <button class="review-form__btn review-form__btn--next" data-form-next type="button">
              ${escapeHtml(t('form_next'))} →
            </button>
          </div>
        </div>`;
    }

    const customerName = cfg().customerName || '';
    const showPhotos   = cfg().showPhotos !== false;
    const maxPhotos    = cfg().maxPhotos || MAX_PHOTOS_DEFAULT;

    return `
      <div class="review-form__step" data-step="2">
        <div class="review-form__field">
          <label class="review-form__label" for="review-body">${escapeHtml(t('form_body_label'))} <span class="review-form__required">*</span></label>
          <textarea class="review-form__textarea" id="review-body" maxlength="2000" rows="5"
                    placeholder="${escapeHtml(t('form_body_placeholder') || '')}"></textarea>
          <p class="review-form__error" id="body-error" hidden>${escapeHtml(t('form_body_required'))}</p>
        </div>

        ${showPhotos ? `
        <div class="review-form__field">
          <label class="review-form__label" for="review-photos">
            ${escapeHtml(t('form_photos_label') || 'Photos')}
            <span class="review-form__hint">(${escapeHtml(t('form_photos_hint') || 'optional, up to ' + maxPhotos)})</span>
          </label>
          <div class="review-form__photo-grid" id="photo-grid"></div>
          <label class="review-form__photo-add" for="review-photos" id="photo-add-label">
            <svg viewBox="0 0 24 24" aria-hidden="true" width="20" height="20">
              <path fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" d="M12 5v14M5 12h14"/>
            </svg>
            <span>${escapeHtml(t('form_photos_add') || 'Add photos')}</span>
          </label>
          <input class="review-form__photo-input" id="review-photos" type="file"
                 accept="image/jpeg,image/png,image/webp" multiple>
          <p class="review-form__error" id="photos-error" hidden></p>
        </div>` : ''}

        <div class="review-form__field">
          <label class="review-form__label" for="review-name">${escapeHtml(t('form_name_label'))} <span class="review-form__required">*</span></label>
          <input class="review-form__input" id="review-name" type="text" maxlength="100"
                 value="${escapeHtml(customerName)}"
                 placeholder="${escapeHtml(t('form_name_placeholder') || '')}">
          <p class="review-form__error" id="name-error" hidden>${escapeHtml(t('form_name_required') || 'Please tell us your name')}</p>
        </div>

        <div class="review-form__actions">
          <button class="review-form__btn review-form__btn--back" data-form-back type="button">
            ← ${escapeHtml(t('form_back'))}
          </button>
          <button class="review-form__btn review-form__btn--submit" data-form-submit type="button">
            ${escapeHtml(t('form_submit'))}
          </button>
        </div>
      </div>`;
  }

  #activateStep(step) {
    this.#overlay.querySelectorAll('.review-form__step').forEach(el => {
      el.classList.toggle('is-active', el.dataset.step === String(step));
    });
    this.#overlay.querySelectorAll('[data-progress-step]').forEach(el => {
      const n = parseInt(el.dataset.progressStep, 10);
      el.classList.toggle('is-active', n <= step);
    });
    this.#step = step;
    this.#overlay.querySelector('.review-form__drawer')?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  #updateStars() {
    this.#overlay?.querySelectorAll('[data-star]').forEach(btn => {
      const n = parseInt(btn.dataset.star, 10);
      btn.classList.toggle('is-filled', n <= this.#rating);
      btn.setAttribute('aria-pressed', String(n <= this.#rating));
    });
    this.#hideError('rating-error');
  }

  #hideError(id) {
    const el = this.#overlay?.querySelector(`#${id}`);
    if (el) el.hidden = true;
  }

  #showError(id, message) {
    const el = this.#overlay?.querySelector(`#${id}`);
    if (!el) return;
    if (message) el.textContent = message;
    el.hidden = false;
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  #nextStep() {
    if (this.#step !== 1) return;
    let valid = true;
    if (this.#rating === 0) {
      this.#showError('rating-error');
      valid = false;
    }
    const title = this.#overlay?.querySelector('#review-title')?.value?.trim() || '';
    if (!title) {
      this.#showError('title-error');
      valid = false;
    }
    if (!valid) return;
    this.#activateStep(2);
  }

  #prevStep() {
    if (this.#step > 1) this.#activateStep(this.#step - 1);
  }

  async #handlePhotos(fileList) {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const maxPhotos = cfg().maxPhotos || MAX_PHOTOS_DEFAULT;
    const maxBytes  = cfg().maxPhotoBytes || MAX_PHOTO_BYTES_DEFAULT;
    const remaining = maxPhotos - this.#photos.length;
    if (remaining <= 0) {
      this.#showError('photos-error', t('form_photos_max') || `Maximum ${maxPhotos} photos`);
      return;
    }

    const toProcess = files.slice(0, remaining);
    for (const file of toProcess) {
      try {
        const resized = await resizePhoto(file, maxBytes);
        this.#photos.push({
          file: resized,
          previewUrl: URL.createObjectURL(resized),
        });
      } catch (err) {
        this.#showError('photos-error', t('form_photos_error') || 'Could not process one of the photos');
      }
    }
    this.#renderPhotoGrid();
    // Reset input so picking the same file again still triggers change
    const input = this.#overlay?.querySelector('#review-photos');
    if (input) input.value = '';
  }

  #renderPhotoGrid() {
    const grid = this.#overlay?.querySelector('#photo-grid');
    if (!grid) return;
    grid.innerHTML = this.#photos.map((p, i) => `
      <div class="review-form__photo-thumb">
        <img src="${escapeHtml(p.previewUrl)}" alt="" loading="lazy">
        <button class="review-form__photo-remove" data-photo-remove="${i}" type="button"
                aria-label="${escapeHtml(t('form_photos_remove') || 'Remove photo')}">✕</button>
      </div>
    `).join('');

    const maxPhotos = cfg().maxPhotos || MAX_PHOTOS_DEFAULT;
    const addBtn = this.#overlay?.querySelector('#photo-add-label');
    if (addBtn) addBtn.style.display = (this.#photos.length >= maxPhotos) ? 'none' : '';
  }

  #removePhoto(e) {
    const idx = parseInt(e.target.closest('[data-photo-remove]').dataset.photoRemove, 10);
    if (Number.isNaN(idx)) return;
    const removed = this.#photos.splice(idx, 1)[0];
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    this.#renderPhotoGrid();
    this.#hideError('photos-error');
  }

  #saveDraft() {
    const productId = cfg().productId;
    if (!productId) return;
    ReviewDraft.save(productId, {
      rating: this.#rating,
      title:  this.#overlay?.querySelector('#review-title')?.value || '',
      body:   this.#overlay?.querySelector('#review-body')?.value  || '',
      name:   this.#overlay?.querySelector('#review-name')?.value  || '',
    });
  }

  #fillDraft(draft) {
    if (draft.rating) { this.#rating = draft.rating; this.#updateStars(); }
    const titleEl = this.#overlay?.querySelector('#review-title');
    const bodyEl  = this.#overlay?.querySelector('#review-body');
    const nameEl  = this.#overlay?.querySelector('#review-name');
    if (titleEl && draft.title) titleEl.value = draft.title;
    if (bodyEl  && draft.body)  bodyEl.value  = draft.body;
    if (nameEl  && draft.name)  nameEl.value  = draft.name;
  }

  async #refreshNonce() {
    const config = cfg();
    const sessionMessage = t('form_error_session') || 'Session expired — please refresh the page and try again.';
    if (!config.nonce || !config.productId) {
      this.#saveDraft();
      showToast(sessionMessage, 'error');
      return false;
    }
    const nonceAge = Date.now() / 1000 - parseInt(config.nonce, 10);
    if (!Number.isFinite(nonceAge)) {
      this.#saveDraft();
      showToast(sessionMessage, 'error');
      return false;
    }
    if (nonceAge < 240) return true;
    // The public refresh endpoint is intentionally guest-only. A logged-in
    // customer's identity can only be signed by Shopify Liquid, so an expired
    // customer session must refresh the page rather than downgrade or accept a
    // caller-supplied customer ID.
    if (config.customerLoggedIn) {
      this.#saveDraft();
      showToast(sessionMessage, 'error');
      return false;
    }
    try {
      const base = config.apiBase || 'https://reviews.3eeed.app';
      const params = new URLSearchParams({
        product_id:  config.productId,
      });
      const res = await fetch(`${base}/api/reviews/nonce?${params}`);
      if (!res.ok) throw new Error('nonce_refresh_failed');
      const data = await res.json();
      if (data.customer_id === 'guest' && data.nonce && data.signature) {
        window.reviewConfig = {
          ...window.reviewConfig,
          customerId: data.customer_id || 'guest',
          nonce: data.nonce,
          hmac: data.signature,
        };
        return true;
      }
    } catch {}
    this.#saveDraft();
    showToast(sessionMessage, 'error');
    return false;
  }

  async #submit(e) {
    if (this.#submitting) return;
    const submitBtn = e.target.closest('[data-form-submit]');

    if (cfg().signingReady === false) {
      showToast(
        t('form_error_session') || 'Review signing is temporarily unavailable. Please try again later.',
        'error',
      );
      return;
    }

    const body = this.#overlay?.querySelector('#review-body')?.value?.trim() || '';
    const name = this.#overlay?.querySelector('#review-name')?.value?.trim() || '';
    let valid = true;
    if (!body) { this.#showError('body-error'); valid = false; }
    if (!name) { this.#showError('name-error'); valid = false; }
    if (!valid) return;

    if (!(await this.#refreshNonce())) return;
    this.#submitting = true;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = t('form_submitting');
    }

    const lang = (document.documentElement.lang || 'en').toLowerCase().slice(0, 2);
    const language = (lang === 'ar') ? 'ar' : 'en';

    const formData = new FormData();
    formData.append('customer_id',  cfg().customerId  || 'guest');
    formData.append('product_id',   cfg().productId   || 0);
    formData.append('nonce',        cfg().nonce        || '');
    formData.append('signature',    cfg().hmac         || '');
    formData.append('rating',       this.#rating);
    formData.append('title',        this.#overlay?.querySelector('#review-title')?.value?.trim() || '');
    formData.append('body',         body);
    formData.append('customer_name', name);
    formData.append('author_name',  name);
    formData.append('language',     language);
    this.#photos.forEach(p => formData.append('photos', p.file, p.file.name));

    try {
      const base = cfg().apiBase || 'https://reviews.3eeed.app';
      const res  = await fetch(`${base}/api/reviews/submit`, { method: 'POST', body: formData });
      let payload = null;
      try { payload = await res.json(); } catch {}

      if (!res.ok) {
        const errKey = payload?.error || `http_${res.status}`;
        const errMsg = this.#errorMessage(errKey);
        throw new Error(errMsg);
      }

      ReviewDraft.clear(cfg().productId);
      this.#showSuccess();
    } catch (err) {
      this.#submitting = false;
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = t('form_submit');
      }
      showToast(err?.message || t('form_error_generic'), 'error');
    }
  }

  #errorMessage(errKey) {
    const map = {
      hmac_invalid: t('form_error_session') || 'Session expired — please refresh the page and try again.',
      invalid_signature: t('form_error_session') || 'Session expired — please refresh the page and try again.',
      title_invalid: t('form_title_required') || 'Title is required (max 120 characters).',
      body_invalid: t('form_body_required') || 'Review body is required (max 2000 characters).',
      language_invalid: t('form_error_generic') || 'Language not supported.',
      name_required: t('form_name_required') || 'Please tell us your name.',
      too_many_photos: t('form_photos_max') || 'Too many photos.',
      rate_limited: t('form_error_rate_limit') || 'Too many submissions — please wait a few minutes.',
    };
    return map[errKey] || t('form_error_generic') || 'Something went wrong. Please try again.';
  }

  #showSuccess() {
    // Reset flag so the X can close the dialog manually
    this.#submitting = false;
    this.#overlay?.querySelectorAll('.review-form__step').forEach(el => el.classList.remove('is-active'));
    this.#overlay?.querySelector('.review-form__progress')?.setAttribute('hidden', '');
    const success = this.#overlay?.querySelector('.review-form__success');
    if (success) success.hidden = false;

    // Replace plain X with countdown variant: outer SVG ring shrinks over 5s
    const closeBtn = this.#overlay?.querySelector('.review-form__close');
    if (closeBtn) {
      closeBtn.classList.add('review-form__close--countdown');
      closeBtn.innerHTML = `
        <svg class="review-form__close-ring" viewBox="0 0 32 32" aria-hidden="true">
          <circle class="review-form__close-ring-track" cx="16" cy="16" r="14"></circle>
          <circle class="review-form__close-ring-progress" cx="16" cy="16" r="14"></circle>
        </svg>
        <span class="review-form__close-x" aria-hidden="true">✕</span>`;
    }

    const AUTO_CLOSE_MS = 5000;
    this.#successTimer = setTimeout(() => {
      this.close();
    }, AUTO_CLOSE_MS);
  }
}

customElements.define('review-form', ReviewForm);
